/*
  pn-load-more — "Voir plus" for the collection grid (C1).

  Progressive enhancement, deliberately. The server still renders Dawn's numbered pagination inside
  this element; the component only replaces it once it knows it can do the job. With JS off, broken,
  or still loading, the numbered links are there and every product stays reachable — including to a
  crawler, which matters because these are indexed category pages.

  Pages are fetched with `?section_id=`, so Shopify returns just this section rather than a whole
  document to throw away.
*/
class PnLoadMore extends HTMLElement {
  connectedCallback() {
    this.nextUrl = this.dataset.nextUrl || '';
    if (!this.nextUrl) return;

    this.grid = document.getElementById('product-grid');
    if (!this.grid) return;

    this.pagination = this.querySelector('.pagination-wrapper, nav');
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'button button--secondary pn-load-more__button';
    this.button.textContent = this.dataset.label || 'Load more';
    this.button.addEventListener('click', () => this.loadNext());

    this.status = document.createElement('p');
    this.status.className = 'pn-load-more__status';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');

    // Only now is the numbered pagination redundant.
    if (this.pagination) this.pagination.hidden = true;
    this.append(this.button, this.status);
  }

  async loadNext() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    this.button.disabled = true;
    this.button.textContent = this.dataset.loadingLabel || 'Loading…';

    try {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const incoming = doc.getElementById('product-grid');
      if (!incoming) throw new Error('no grid in response');

      const added = Array.from(incoming.children);
      // Reveal-on-scroll stamps an --animation-order per item; continuing the count from the
      // existing children keeps the new rows cascading in sequence instead of restarting at 1.
      const offset = this.grid.children.length;
      added.forEach((item, i) => {
        item.style.setProperty('--animation-order', String(offset + i + 1));
        this.grid.appendChild(item);
      });

      // The next page's own "next" link is the only reliable source for where to go after this.
      const nextLink = doc.querySelector('.pagination__item--next, a[rel="next"]');
      this.nextUrl = nextLink ? nextLink.getAttribute('href') : '';

      this.status.textContent = `${this.grid.children.length}`;
      // Move focus to the first new card so a keyboard user is not dropped back at the top.
      const firstNew = added[0] && added[0].querySelector('a');
      if (firstNew) firstNew.setAttribute('tabindex', '-1');

      if (this.nextUrl) {
        this.button.disabled = false;
        this.button.textContent = this.dataset.label || 'Load more';
      } else {
        this.button.remove();
        this.status.textContent = this.dataset.doneLabel || '';
      }
    } catch (err) {
      // Give the shopper back something that definitely works rather than a dead button.
      if (this.pagination) this.pagination.hidden = false;
      this.button.remove();
      this.status.textContent = '';
      console.error('pn-load-more:', err);
    } finally {
      this.loading = false;
    }
  }
}

if (!customElements.get('pn-load-more')) {
  customElements.define('pn-load-more', PnLoadMore);
}
